import type { PaymentMethod } from './catalog.js';

/**
 * Phase 5: ZIMRA fiscalisation (FDMS, Fiscal Device Gateway API v7.x).
 *
 * Pure rules shared by the API and its tests: the canonical strings the device signs, fiscal
 * day counters, and the receipt verification QR. Amounts are integer cents throughout; tax
 * rates are basis points (1550 = 15.50%). Cross-checked against ZIMRA's published examples
 * and independent conformance vectors (see apps/api/test/fiscal.test.ts).
 */

export type ReceiptType = 'FiscalInvoice' | 'CreditNote' | 'DebitNote';
export type MoneyType = 'Cash' | 'Card' | 'MobileWallet' | 'Coupon' | 'Credit' | 'BankTransfer' | 'Other';
export type FiscalEnvironment = 'simulator' | 'test' | 'production';

export const FDMS_URLS: Record<Exclude<FiscalEnvironment, 'simulator'>, string> = {
  test: 'https://fdmsapitest.zimra.co.zw',
  production: 'https://fdmsapi.zimra.co.zw',
};

/** How each way of paying is reported to ZIMRA. */
export const MONEY_TYPES: Record<PaymentMethod, MoneyType> = {
  cash: 'Cash',
  card: 'Card',
  ecocash: 'MobileWallet',
  onemoney: 'MobileWallet',
  innbucks: 'MobileWallet',
  omari: 'MobileWallet',
  bank_transfer: 'BankTransfer',
  account: 'Credit',
  deposit: 'Other',
};

/** Wire order of MoneyTypeEnum: balance counters sort by this, not alphabetically. */
const MONEY_TYPE_ORDER: MoneyType[] = ['Cash', 'Card', 'MobileWallet', 'Coupon', 'Credit', 'BankTransfer', 'Other'];

export interface FiscalTax {
  taxID: number;
  taxCode?: string | null;
  /** Basis points; null for exempt. */
  taxPercentBp: number | null;
  taxAmountCents: number;
  salesAmountWithTaxCents: number;
}

/** 1550 → "15.50", 0 → "0.00". Exact, no floating point. */
export function formatTaxPercent(bp: number): string {
  const sign = bp < 0 ? '-' : '';
  const a = Math.abs(bp);
  return `${sign}${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`;
}

function byCodePoint(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Tax order in signing strings: taxID ascending, then taxCode. */
export function sortTaxes<T extends { taxID: number; taxCode?: string | null }>(taxes: T[]): T[] {
  return [...taxes].sort((a, b) => a.taxID - b.taxID || byCodePoint(a.taxCode ?? '', b.taxCode ?? ''));
}

/**
 * Receipt device-signature input (spec section 13.2.1):
 * deviceID + RECEIPTTYPE + CURRENCY + receiptGlobalNo + receiptDate + receiptTotal(cents)
 * + for each tax [taxCode + taxPercent(2dp, empty when exempt) + taxAmount(cents) + salesAmountWithTax(cents)]
 * + previousReceiptHash (left out on the first receipt of a fiscal day).
 */
export function receiptSigningString(r: {
  deviceId: number;
  receiptType: ReceiptType;
  currency: string;
  receiptGlobalNo: number;
  receiptDate: string;
  receiptTotalCents: number;
  taxes: FiscalTax[];
  previousReceiptHash?: string | null;
}): string {
  const taxes = sortTaxes(r.taxes)
    .map((t) => `${t.taxCode ?? ''}${t.taxPercentBp == null ? '' : formatTaxPercent(t.taxPercentBp)}${t.taxAmountCents}${t.salesAmountWithTaxCents}`)
    .join('');
  return `${r.deviceId}${r.receiptType.toUpperCase()}${r.currency.toUpperCase()}${r.receiptGlobalNo}${r.receiptDate}${r.receiptTotalCents}${taxes}${r.previousReceiptHash ?? ''}`;
}

export type CounterType = 'SaleByTax' | 'SaleTaxByTax' | 'CreditNoteByTax' | 'CreditNoteTaxByTax' | 'DebitNoteByTax' | 'DebitNoteTaxByTax' | 'BalanceByMoneyType';
const COUNTER_ORDER: CounterType[] = ['SaleByTax', 'SaleTaxByTax', 'CreditNoteByTax', 'CreditNoteTaxByTax', 'DebitNoteByTax', 'DebitNoteTaxByTax', 'BalanceByMoneyType'];

export interface FiscalCounter {
  type: CounterType;
  currency: string;
  taxID?: number | null;
  taxPercentBp?: number | null;
  moneyType?: MoneyType | null;
  valueCents: number;
}

/** Counters in the order ZIMRA expects; zero counters are never reported. */
export function sortCounters(counters: FiscalCounter[]): FiscalCounter[] {
  return counters
    .filter((c) => c.valueCents !== 0)
    .sort(
      (a, b) =>
        COUNTER_ORDER.indexOf(a.type) - COUNTER_ORDER.indexOf(b.type) ||
        byCodePoint(a.currency, b.currency) ||
        (a.taxID ?? -1) - (b.taxID ?? -1) ||
        MONEY_TYPE_ORDER.indexOf(a.moneyType ?? 'Other') - MONEY_TYPE_ORDER.indexOf(b.moneyType ?? 'Other'),
    );
}

/**
 * Fiscal day device-signature input (spec section 13.3.1):
 * deviceID + fiscalDayNo + fiscalDayDate(YYYY-MM-DD) + for each non-zero counter in order
 * [TYPE + CURRENCY + (taxPercent 2dp | MONEYTYPE) + value(cents)].
 */
export function fiscalDaySigningString(deviceId: number, fiscalDayNo: number, fiscalDayDate: string, counters: FiscalCounter[]): string {
  const body = sortCounters(counters)
    .map(
      (c) =>
        `${c.type.toUpperCase()}${c.currency.toUpperCase()}${c.taxPercentBp == null ? '' : formatTaxPercent(c.taxPercentBp)}${c.moneyType ? c.moneyType.toUpperCase() : ''}${c.valueCents}`,
    )
    .join('');
  return `${deviceId}${fiscalDayNo}${fiscalDayDate}${body}`;
}

/** Adds one receipt's totals into the day's counters. Credit note amounts are already negative. */
export function addToCounters(
  counters: FiscalCounter[],
  receipt: { receiptType: ReceiptType; currency: string; taxes: FiscalTax[]; payments: { moneyType: MoneyType; amountCents: number }[] },
): FiscalCounter[] {
  const bump = (match: Omit<FiscalCounter, 'valueCents'>, delta: number) => {
    const found = counters.find(
      (c) => c.type === match.type && c.currency === match.currency && (c.taxID ?? null) === (match.taxID ?? null) && (c.moneyType ?? null) === (match.moneyType ?? null),
    );
    if (found) found.valueCents += delta;
    else counters.push({ ...match, valueCents: delta });
  };
  const [byTax, taxByTax]: [CounterType, CounterType] =
    receipt.receiptType === 'FiscalInvoice' ? ['SaleByTax', 'SaleTaxByTax'] : receipt.receiptType === 'CreditNote' ? ['CreditNoteByTax', 'CreditNoteTaxByTax'] : ['DebitNoteByTax', 'DebitNoteTaxByTax'];
  for (const t of receipt.taxes) {
    bump({ type: byTax, currency: receipt.currency, taxID: t.taxID, taxPercentBp: t.taxPercentBp }, t.salesAmountWithTaxCents);
    bump({ type: taxByTax, currency: receipt.currency, taxID: t.taxID, taxPercentBp: t.taxPercentBp }, t.taxAmountCents);
  }
  for (const p of receipt.payments) bump({ type: 'BalanceByMoneyType', currency: receipt.currency, moneyType: p.moneyType }, p.amountCents);
  return counters;
}

/** Local date-time in Harare as FDMS expects it: YYYY-MM-DDTHH:mm:ss, no timezone. */
export function fiscalDateTime(d: Date): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Harare', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

/**
 * Data in the verification QR printed on every fiscal receipt (spec section 11):
 * qrUrl + deviceID (10 digits) + receipt date (ddMMyyyy) + receiptGlobalNo (10 digits)
 * + the first 16 hex characters of MD5 over the receipt's device signature bytes.
 */
export function receiptQrData(qrUrl: string, deviceId: number, receiptDate: string, receiptGlobalNo: number, md5First16: string): string {
  const base = qrUrl.endsWith('/') ? qrUrl : `${qrUrl}/`;
  const [y, m, d] = receiptDate.slice(0, 10).split('-');
  return `${base}${String(deviceId).padStart(10, '0')}${d}${m}${y}${String(receiptGlobalNo).padStart(10, '0')}${md5First16.toLowerCase()}`;
}

/** The same 16 characters printed for manual checking: XXXX-XXXX-XXXX-XXXX. */
export function verificationCode(md5First16: string): string {
  return md5First16.toUpperCase().match(/.{4}/g)!.join('-');
}

/** Receipt layout fields every fiscal invoice shows (spec section 11 and the receipt rules). */
export interface FiscalReceiptStamp {
  environment: FiscalEnvironment;
  deviceId: number;
  deviceSerialNo: string;
  fiscalDayNo: number;
  receiptCounter: number;
  receiptGlobalNo: number;
  receiptType: ReceiptType;
  verificationCode: string;
  qrData: string;
  status: 'queued' | 'accepted' | 'accepted_with_errors' | 'rejected';
  taxpayerName: string | null;
  taxpayerTIN: string | null;
  vatNumber: string | null;
}
